import React, { useState, useRef, useCallback } from "react";
import { DOCUMENT_CATEGORIES } from "./ItemModal";
import {
  CSV_IMPORT_MAX_FILE_SIZE_MB,
  CSV_IMPORT_MAX_ROWS,
} from "../constants/expiry";

// ── Types ─────────────────────────────────────────────────────────────────────
interface ParsedRow {
  rowNum: number;
  name: string;
  category: string;
  owner_email: string;
  expiry_date: string;
  notes: string;
  status: "valid" | "warning" | "error";
  issues: string[];
}

interface ImportModalProps {
  onClose: () => void;
  /** Receives the (potentially edited) reconstructed CSV text. */
  onImport: (csvText: string) => Promise<void>;
}

type Step = "upload" | "review" | "fix" | "importing" | "done";

// ── Constants ─────────────────────────────────────────────────────────────────
const KNOWN_CATEGORIES = new Set<string>(DOCUMENT_CATEGORIES);

/** All header aliases recognised by the backend (used for header-health check) */
const KNOWN_HEADERS = new Set([
  "name",
  "document name",
  "doc name",
  "document",
  "title",
  "category",
  "type",
  "doc type",
  "document type",
  "expiry date",
  "expiry",
  "expiration",
  "expiration date",
  "expires",
  "expire date",
  "valid until",
  "valid thru",
  "expiry date (yyyy-mm-dd)",
  "owner email",
  "email",
  "owner",
  "owner_email",
  "notes",
  "description",
  "remark",
  "remarks",
  "comment",
  "comments",
]);
const NAME_HEADERS = new Set([
  "name",
  "document name",
  "doc name",
  "document",
  "title",
]);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Helpers ───────────────────────────────────────────────────────────────────
const normaliseDate = (raw: string): string => {
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) return raw.trim();
  const parts = raw.trim().split(/[\/\-\.]/);
  if (parts.length === 3) {
    const [a, b, c] = parts;
    if (c.length === 4)
      return `${c}-${b.padStart(2, "0")}-${a.padStart(2, "0")}`;
    if (a.length === 4)
      return `${a}-${b.padStart(2, "0")}-${c.padStart(2, "0")}`;
  }
  return "";
};

const isValidDate = (d: string) => {
  if (!d) return false;
  return !isNaN(new Date(d).getTime());
};

/** Parse a CSV header row into lower-cased column names */
function parseHeaderCols(line: string): string[] {
  const cols: string[] = [];
  let cur = "",
    inQ = false;
  for (const ch of line) {
    if (ch === '"') {
      inQ = !inQ;
    } else if (ch === "," && !inQ) {
      cols.push(cur.trim().toLowerCase());
      cur = "";
    } else cur += ch;
  }
  cols.push(cur.trim().toLowerCase());
  return cols;
}

/**
 * Inspect the header row and return human-readable warnings.
 * Unrecognised columns and a missing Name column are flagged.
 */
function detectHeaderIssues(text: string): string[] {
  const firstLine = text.split(/\r?\n/)[0] ?? "";
  const cols = parseHeaderCols(firstLine);
  const warnings: string[] = [];

  const unrecognized = cols.filter((c) => c && !KNOWN_HEADERS.has(c));
  if (unrecognized.length > 0) {
    warnings.push(
      `Unrecognised column${unrecognized.length > 1 ? "s" : ""}: "${unrecognized.join('", "')}" — these will be ignored during import.`
    );
  }
  if (!cols.some((c) => NAME_HEADERS.has(c))) {
    warnings.push(
      'No "Name" column found — all rows may be saved as drafts. Verify your column headers match the expected format.'
    );
  }
  return warnings;
}

/**
 * Validate a single row's fields.
 * Extracted so both parseCSV (for preview) and the fix step (for live re-validation) can share the same logic.
 */
function validateRowFields(fields: {
  name: string;
  category: string;
  owner_email: string;
  expiry_date: string; // raw or normalised — both work
}): Pick<ParsedRow, "status" | "issues"> {
  const issues: string[] = [];

  if (!fields.name.trim()) issues.push("Name is required");

  const normDate = normaliseDate(fields.expiry_date);
  if (fields.expiry_date && !isValidDate(normDate)) {
    issues.push("Expiry date format not recognised (use YYYY-MM-DD)");
  }
  if (!normDate && !fields.expiry_date) {
    issues.push("No expiry date — document will need attention");
  }
  if (fields.category && !KNOWN_CATEGORIES.has(fields.category)) {
    issues.push("Category not in standard list — will be saved as-is");
  }
  if (fields.owner_email && !EMAIL_RE.test(fields.owner_email)) {
    issues.push("Owner email format is invalid");
  }

  const status: ParsedRow["status"] = issues.some(
    (x) =>
      x.includes("required") ||
      x.includes("not recognised") ||
      x.includes("invalid")
  )
    ? "error"
    : issues.length > 0
      ? "warning"
      : "valid";

  return { status, issues };
}

/** Header → canonical field name (mirrors backend ALIASES) */
const CSV_FIELD_ALIASES: Record<
  string,
  keyof Omit<ParsedRow, "rowNum" | "status" | "issues">
> = {
  // name
  name: "name",
  "document name": "name",
  "doc name": "name",
  document: "name",
  title: "name",
  // category
  category: "category",
  type: "category",
  "doc type": "category",
  "document type": "category",
  // expiry_date
  expiry_date: "expiry_date",
  expiry: "expiry_date",
  expiration: "expiry_date",
  "expiration date": "expiry_date",
  expires: "expiry_date",
  "expire date": "expiry_date",
  "expiry date": "expiry_date",
  "expiry date (yyyy-mm-dd)": "expiry_date",
  "valid until": "expiry_date",
  "valid thru": "expiry_date",
  // owner_email
  owner_email: "owner_email",
  "owner email": "owner_email",
  email: "owner_email",
  owner: "owner_email",
  // notes
  notes: "notes",
  description: "notes",
  remark: "notes",
  remarks: "notes",
  comment: "notes",
  comments: "notes",
};

/** Split a single CSV line respecting quoted fields */
function splitCSVLine(line: string): string[] {
  const cols: string[] = [];
  let cur = "",
    inQ = false;
  for (const ch of line) {
    if (ch === '"') {
      inQ = !inQ;
    } else if (ch === "," && !inQ) {
      cols.push(cur.trim());
      cur = "";
    } else cur += ch;
  }
  cols.push(cur.trim());
  return cols;
}

/** Parse CSV text into preview rows using header-based column mapping */
const parseCSV = (text: string): ParsedRow[] => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];

  // Build col-index → field map from the header row
  const headerCols = splitCSVLine(lines[0]);
  const fieldMap: Record<
    number,
    keyof Omit<ParsedRow, "rowNum" | "status" | "issues">
  > = {};
  headerCols.forEach((h, idx) => {
    const alias = CSV_FIELD_ALIASES[h.toLowerCase().trim()];
    if (alias) fieldMap[idx] = alias;
  });

  return lines.slice(1).map((line, i) => {
    const cols = splitCSVLine(line);
    let name = "",
      category = "",
      owner_email = "",
      expiry_date_raw = "",
      notes = "";

    cols.forEach((val, idx) => {
      const field = fieldMap[idx];
      if (!field) return;
      if (field === "name") name = val;
      else if (field === "category") category = val;
      else if (field === "expiry_date") expiry_date_raw = val;
      else if (field === "owner_email") owner_email = val;
      else if (field === "notes") notes = val;
    });

    const expiry_date = normaliseDate(expiry_date_raw);
    const { status, issues } = validateRowFields({
      name,
      category,
      owner_email,
      expiry_date: expiry_date_raw,
    });
    return {
      rowNum: i + 2,
      name,
      category,
      owner_email,
      expiry_date,
      notes,
      status,
      issues,
    };
  });
};

/**
 * Reconstruct a CSV string from ParsedRow[] using canonical column headers
 * that the backend's `parseCsvLenient` will correctly recognise.
 * Only non-error rows are included — error rows are truly excluded.
 */
/** Canonical headers used when reconstructing CSV for the backend */
const CSV_EXPORT_HEADERS = [
  "Name",
  "Category",
  "Expiry Date (YYYY-MM-DD)",
  "Owner Email",
  "Notes",
] as const;

function rowsToCsv(rows: ParsedRow[]): string {
  const esc = (v: string) =>
    v && (v.includes(",") || v.includes('"') || v.includes("\n"))
      ? `"${v.replace(/"/g, '""')}"`
      : (v ?? "");
  const header = CSV_EXPORT_HEADERS.join(",");
  const dataRows = rows
    .filter((r) => r.status !== "error")
    .map((r) => {
      const category =
        r.category === "__others__" || !r.category?.trim()
          ? "Others"
          : r.category;
      return [r.name, category, r.expiry_date, r.owner_email, r.notes]
        .map(esc)
        .join(",");
    });
  return [header, ...dataRows].join("\n");
}

const downloadTemplate = () => {
  const rows = [
    CSV_EXPORT_HEADERS.join(","),
    // name, category, expiry date, owner email, notes
    [
      "Trade License",
      "Licenses & Permits",
      "2027-01-15",
      "alice@company.com",
      "Annual renewal",
    ].join(","),
    [
      "Insurance Policy",
      "Insurance",
      "2026-06-30",
      "carol@company.com",
      "Comprehensive coverage",
    ].join(","),
    ["Vendor Contract", "Contracts", "2027-03-01", "", "Quarterly review"].join(
      ","
    ),
  ].join("\n");
  const blob = new Blob([rows], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "sentrilog-template.csv";
  a.click();
  URL.revokeObjectURL(url);
};

// ── Status badge ──────────────────────────────────────────────────────────────
const RowBadge: React.FC<{ status: ParsedRow["status"] }> = ({ status }) => {
  const map = {
    valid: { bg: "bg-[#DCFCE7]", text: "text-[#15803D]", label: "Ready" },
    warning: { bg: "bg-[#FEF3C7]", text: "text-[#D97706]", label: "Draft" },
    error: { bg: "bg-[#FEE2E2]", text: "text-[#DC2626]", label: "Error" },
  }[status];
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${map.bg} ${map.text}`}
    >
      {map.label}
    </span>
  );
};

/** Returns true if any issue string contains one of the given keywords */
function hasIssueKeyword(issues: string[], ...keywords: string[]): boolean {
  return issues.some((i) => keywords.some((k) => i.toLowerCase().includes(k)));
}

// ── Shared input class helper ─────────────────────────────────────────────────
function inputCls(hasError: boolean, hasWarning: boolean): string {
  const base =
    "w-full rounded border py-1 px-2 text-xs text-[#111827] placeholder:text-[#9CA3AF] bg-white focus:outline-none focus:ring-1 transition-colors";
  if (hasError) return `${base} border-[#DC2626] focus:ring-[#DC2626]`;
  if (hasWarning) return `${base} border-[#D97706] focus:ring-[#D97706]`;
  return `${base} border-[#E5E7EB] focus:ring-[#2563EB]`;
}

// ── Main Modal ────────────────────────────────────────────────────────────────
const ImportModal: React.FC<ImportModalProps> = ({ onClose, onImport }) => {
  const [step, setStep] = useState<Step>("upload");
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [editRows, setEditRows] = useState<ParsedRow[]>([]);
  const [headerWarnings, setHeaderWarnings] = useState<string[]>([]);
  const [importError, setImportError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  // ── Inline-edit state (review table) ─────────────────────────────────────
  const [editingRowNum, setEditingRowNum] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Omit<ParsedRow, "rowNum">>({
    name: "",
    category: "",
    owner_email: "",
    expiry_date: "",
    notes: "",
    status: "valid",
    issues: [],
  });

  // ── Derived counts ────────────────────────────────────────────────────────
  const validCount = rows.filter((r) => r.status === "valid").length;
  const warningCount = rows.filter((r) => r.status === "warning").length;
  const errorCount = rows.filter((r) => r.status === "error").length;
  const importable = rows.filter((r) => r.status !== "error");
  const problemCount = errorCount + warningCount;

  // Fix step derived
  const fixedInStep = editRows.filter((r) => r.status === "valid").length;
  const stillProblemCount = editRows.filter((r) => r.status !== "valid").length;

  // ── File handling ─────────────────────────────────────────────────────────
  const processFile = useCallback((file: File) => {
    if (!file.name.endsWith(".csv")) {
      setImportError("Only .csv files are supported.");
      return;
    }
    if (file.size > CSV_IMPORT_MAX_FILE_SIZE_MB * 1024 * 1024) {
      setImportError(`File must be under ${CSV_IMPORT_MAX_FILE_SIZE_MB} MB.`);
      return;
    }
    setImportError("");
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setHeaderWarnings(detectHeaderIssues(text));
      const parsed = parseCSV(text);
      if (parsed.length === 0) {
        setImportError(
          "No data rows found. Make sure the file has a header row and at least one data row."
        );
        return;
      }
      if (parsed.length > CSV_IMPORT_MAX_ROWS) {
        setImportError(
          `Too many rows — this file has ${parsed.length} data rows but the limit is ${CSV_IMPORT_MAX_ROWS}. Split the file into smaller batches and try again.`
        );
        return;
      }
      setRows(parsed);
      setStep("review");
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const resetUpload = () => {
    setStep("upload");
    setRows([]);
    setEditRows([]);
    setHeaderWarnings([]);
    setFileName("");
    setImportError("");
    setEditingRowNum(null);
  };

  // ── Inline row editing (review table) ─────────────────────────────────────
  const startEdit = (row: ParsedRow) => {
    setEditingRowNum(row.rowNum);
    setEditDraft({
      name: row.name,
      category: row.category,
      owner_email: row.owner_email,
      expiry_date: row.expiry_date,
      notes: row.notes,
      status: row.status,
      issues: row.issues,
    });
  };

  const updateDraftField = (
    field: "name" | "category" | "owner_email" | "expiry_date" | "notes",
    value: string
  ) => {
    setEditDraft((prev) => {
      const updated = { ...prev, [field]: value };
      const { status, issues } = validateRowFields({
        name: updated.name,
        category: updated.category,
        owner_email: updated.owner_email,
        expiry_date: updated.expiry_date,
      });
      return { ...updated, status, issues };
    });
  };

  const saveEdit = () => {
    if (editingRowNum === null) return;
    setRows((prev) =>
      prev.map((r) => (r.rowNum === editingRowNum ? { ...r, ...editDraft } : r))
    );
    setEditingRowNum(null);
  };

  const cancelEdit = () => setEditingRowNum(null);

  // ── Fix step (bulk editor for all problem rows) ───────────────────────────
  const openFix = () => {
    setEditingRowNum(null); // close any inline edit first
    setEditRows(
      rows.filter((r) => r.status !== "valid").map((r) => ({ ...r }))
    );
    setStep("fix");
  };

  const updateEditField = (
    rowNum: number,
    field: "name" | "category" | "owner_email" | "expiry_date" | "notes",
    value: string
  ) => {
    setEditRows((prev) =>
      prev.map((r) => {
        if (r.rowNum !== rowNum) return r;
        const updated = { ...r, [field]: value };
        const { status, issues } = validateRowFields({
          name: updated.name,
          category: updated.category,
          owner_email: updated.owner_email,
          expiry_date: updated.expiry_date,
        });
        return { ...updated, status, issues };
      })
    );
  };

  /** Merge fixed rows back into the main rows list, then return to review. */
  const applyFixes = () => {
    setRows((prev) =>
      prev.map((r) => editRows.find((e) => e.rowNum === r.rowNum) ?? r)
    );
    setStep("review");
  };

  // ── Confirm import ────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    const csv = rowsToCsv(rows); // error rows are excluded inside rowsToCsv
    setStep("importing");
    try {
      await onImport(csv);
      setStep("done");
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ??
        "Something went wrong during import. Please try again.";
      setImportError(msg);
      setStep("review");
    }
  };

  // ── Modal width varies by step ────────────────────────────────────────────
  const panelWidth =
    step === "fix"
      ? "sm:max-w-4xl"
      : step === "review"
        ? "sm:max-w-3xl"
        : "sm:max-w-2xl";

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop — intentionally no onClick; modal closes only via ✕ or Cancel */}
        <div className="fixed inset-0 bg-[#111827] bg-opacity-40 transition-opacity" />

        {/* Panel */}
        <div
          className={`inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-4 sm:align-middle ${panelWidth} sm:w-full`}
        >
          {/* ── Header ─────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#EFF6FF] flex items-center justify-center">
                <svg
                  className="h-5 w-5 text-[#2563EB]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-base font-semibold text-[#111827]">
                  Import Documents
                </h2>
                <p className="text-xs text-[#9CA3AF] mt-0.5">
                  {step === "upload" &&
                    "Upload a CSV file to import your existing documents"}
                  {step === "review" &&
                    `Reviewing ${rows.length} rows from "${fileName}"`}
                  {step === "fix" &&
                    `Fixing ${editRows.length} problem row${editRows.length !== 1 ? "s" : ""} — edit inline and apply`}
                  {step === "importing" && "Importing your documents…"}
                  {step === "done" &&
                    `Successfully imported ${importable.length} documents`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F3F4F6] transition-colors"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* ── Step: Upload ────────────────────────────────────────────── */}
          {step === "upload" && (
            <div className="px-6 py-8 space-y-6">
              {/* Drop zone */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={`relative border-2 border-dashed rounded-xl p-14 text-center cursor-pointer transition-all ${
                  isDragging
                    ? "border-[#2563EB] bg-[#EFF6FF]"
                    : "border-[#D1D5DB] hover:border-[#93C5FD] hover:bg-[#F8FAFF]"
                }`}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileInput}
                />
                <div className="flex flex-col items-center gap-3">
                  <div
                    className={`h-14 w-14 rounded-full flex items-center justify-center transition-colors ${isDragging ? "bg-[#DBEAFE]" : "bg-[#F3F4F6]"}`}
                  >
                    <svg
                      className={`h-7 w-7 ${isDragging ? "text-[#2563EB]" : "text-[#9CA3AF]"}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#111827]">
                      {isDragging
                        ? "Drop your CSV file here"
                        : "Drag & drop your CSV file"}
                    </p>
                    <p className="text-xs text-[#9CA3AF] mt-1">
                      or{" "}
                      <span className="text-[#2563EB] font-medium">
                        browse to upload
                      </span>
                    </p>
                  </div>
                  <p className="text-xs text-[#9CA3AF]">
                    CSV only · max {CSV_IMPORT_MAX_FILE_SIZE_MB} MB · up to{" "}
                    {CSV_IMPORT_MAX_ROWS} rows
                  </p>
                </div>
              </div>

              {importError && (
                <p className="text-sm text-[#DC2626] flex items-center gap-1.5">
                  <svg
                    className="h-4 w-4 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {importError}
                </p>
              )}

              {/* Expected columns */}
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4">
                <p className="text-xs font-semibold text-[#374151] mb-2 uppercase tracking-wide">
                  Expected columns
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { col: "Name", req: true, hint: "e.g. Trade License" },
                    { col: "Category", req: false, hint: "e.g. Insurance" },
                    {
                      col: "Expiry Date (YYYY-MM-DD)",
                      req: false,
                      hint: "e.g. 2027-01-15",
                    },
                    {
                      col: "Owner Email",
                      req: false,
                      hint: "e.g. alice@company.com",
                    },
                    { col: "Notes", req: false, hint: "Any freeform text" },
                  ].map(({ col, req, hint }) => (
                    <div key={col} className="flex items-start gap-2">
                      <span
                        className={`mt-0.5 inline-block h-1.5 w-1.5 rounded-full flex-shrink-0 ${req ? "bg-[#DC2626]" : "bg-[#9CA3AF]"}`}
                      />
                      <div>
                        <span className="text-xs font-medium text-[#374151]">
                          {col}
                        </span>
                        {req && (
                          <span className="text-xs text-[#DC2626] ml-1">*</span>
                        )}
                        <p className="text-xs text-[#9CA3AF]">{hint}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Download template */}
              <div className="flex items-center justify-between bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg px-4 py-3">
                <div className="flex items-center gap-2">
                  <svg
                    className="h-4 w-4 text-[#2563EB]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-xs text-[#1E40AF]">
                    Not sure about the format? Start with our template.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="flex items-center gap-1.5 text-xs font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors whitespace-nowrap"
                >
                  <svg
                    className="h-3.5 w-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download Template
                </button>
              </div>
            </div>
          )}

          {/* ── Step: Review ────────────────────────────────────────────── */}
          {step === "review" && (
            <div className="flex flex-col">
              {/* Header warnings (unrecognised columns / missing Name column) */}
              {headerWarnings.length > 0 && (
                <div className="mx-6 mt-4 rounded-lg border border-[#FCD34D] bg-[#FFFBEB] px-4 py-3 space-y-1">
                  <p className="text-xs font-semibold text-[#92400E] flex items-center gap-1.5">
                    <svg
                      className="h-3.5 w-3.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                      />
                    </svg>
                    Column header issues detected
                  </p>
                  {headerWarnings.map((w, i) => (
                    <p key={i} className="text-xs text-[#92400E] pl-5">
                      {w}
                    </p>
                  ))}
                </div>
              )}

              {/* Summary bar */}
              <div className="px-6 py-3 mt-3 bg-[#F9FAFB] border-y border-[#E5E7EB] flex items-center gap-4 flex-wrap">
                <span className="text-xs font-medium text-[#374151]">
                  {rows.length} rows detected
                </span>
                <div className="flex items-center gap-3">
                  {validCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#15803D]">
                      <span className="h-2 w-2 rounded-full bg-[#15803D]" />
                      {validCount} ready
                    </span>
                  )}
                  {warningCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#D97706]">
                      <span className="h-2 w-2 rounded-full bg-[#D97706]" />
                      {warningCount} draft{warningCount > 1 ? "s" : ""}
                    </span>
                  )}
                  {errorCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#DC2626]">
                      <span className="h-2 w-2 rounded-full bg-[#DC2626]" />
                      {errorCount} skipped
                    </span>
                  )}
                </div>

                {/* Fix issues button */}
                {problemCount > 0 && (
                  <button
                    type="button"
                    onClick={openFix}
                    className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold border border-[#D97706] text-[#B45309] bg-[#FFFBEB] hover:bg-[#FEF3C7] transition-colors"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Fix {problemCount} issue{problemCount > 1 ? "s" : ""}
                  </button>
                )}
              </div>

              {/* Legend */}
              {(errorCount > 0 || warningCount > 0) && (
                <div className="px-6 pt-3 pb-1 flex items-center gap-4 text-[10px] text-[#9CA3AF]">
                  {errorCount > 0 && (
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#DC2626]" />
                      Error rows are excluded from import unless fixed
                    </span>
                  )}
                  {warningCount > 0 && (
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#D97706]" />
                      Warning rows are imported as drafts
                    </span>
                  )}
                </div>
              )}

              {/* Preview table */}
              <div className="overflow-y-auto max-h-[520px]">
                <table className="min-w-full text-xs">
                  <thead className="bg-[#F9FAFB] sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2.5 text-left font-semibold text-[#6B7280] uppercase tracking-wider w-8">
                        #
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold text-[#6B7280] uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold text-[#6B7280] uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold text-[#6B7280] uppercase tracking-wider">
                        Expiry Date
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold text-[#6B7280] uppercase tracking-wider">
                        Owner Email
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold text-[#6B7280] uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-3 py-2.5 w-8" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {rows.map((row) => {
                      const isEditing = editingRowNum === row.rowNum;
                      const bgCls = isEditing
                        ? "bg-[#EFF6FF]"
                        : row.status === "error"
                          ? "bg-[#FEF2F2]"
                          : row.status === "warning"
                            ? "bg-[#FFFBEB]"
                            : "bg-white hover:bg-[#F9FAFB]";

                      // For live badge in edit mode
                      const liveStatus = isEditing
                        ? editDraft.status
                        : row.status;
                      const liveIssues = isEditing
                        ? editDraft.issues
                        : row.issues;

                      // Per-field error/warn checks (edit mode)
                      const dNameErr =
                        isEditing &&
                        hasIssueKeyword(editDraft.issues, "name is required");
                      const dDateErr =
                        isEditing &&
                        hasIssueKeyword(editDraft.issues, "not recognised");
                      const dDateWarn =
                        isEditing &&
                        hasIssueKeyword(editDraft.issues, "no expiry date");
                      const dCatWarn =
                        isEditing &&
                        hasIssueKeyword(editDraft.issues, "not in standard");
                      const dEmailErr =
                        isEditing &&
                        hasIssueKeyword(editDraft.issues, "email format");

                      return (
                        <tr key={row.rowNum} className={bgCls}>
                          {/* Row # */}
                          <td className="px-3 py-2 text-[#9CA3AF] align-top pt-3 whitespace-nowrap">
                            {row.rowNum}
                          </td>

                          {/* Name */}
                          <td className="px-3 py-2 align-top">
                            {isEditing ? (
                              <>
                                <input
                                  autoFocus
                                  type="text"
                                  value={editDraft.name}
                                  placeholder="Document name"
                                  onChange={(e) =>
                                    updateDraftField("name", e.target.value)
                                  }
                                  className={inputCls(dNameErr, false)}
                                />
                                {dNameErr && (
                                  <p className="text-[10px] text-[#DC2626] mt-0.5">
                                    Required
                                  </p>
                                )}
                              </>
                            ) : (
                              <span
                                className={`font-medium ${row.name ? "text-[#111827]" : "text-[#DC2626] italic"}`}
                              >
                                {row.name || "missing"}
                              </span>
                            )}
                          </td>

                          {/* Category */}
                          <td className="px-3 py-2 align-top">
                            {isEditing ? (
                              <>
                                <div className="relative">
                                  <select
                                    value={
                                      KNOWN_CATEGORIES.has(editDraft.category)
                                        ? editDraft.category
                                        : editDraft.category
                                          ? "__others__"
                                          : ""
                                    }
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      updateDraftField(
                                        "category",
                                        v === "__others__" ? "__others__" : v
                                      );
                                    }}
                                    className={`${inputCls(false, dCatWarn)} pr-6 cursor-pointer appearance-none`}
                                  >
                                    <option value="">Select category…</option>
                                    {DOCUMENT_CATEGORIES.map((c) => (
                                      <option key={c} value={c}>
                                        {c}
                                      </option>
                                    ))}
                                    <option value="__others__">Custom…</option>
                                  </select>
                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                                    <svg
                                      className="h-3 w-3 text-[#9CA3AF]"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {!KNOWN_CATEGORIES.has(editDraft.category) && (
                                  <input
                                    type="text"
                                    value={
                                      editDraft.category === "__others__"
                                        ? ""
                                        : editDraft.category
                                    }
                                    onChange={(e) =>
                                      updateDraftField(
                                        "category",
                                        e.target.value || "__others__"
                                      )
                                    }
                                    placeholder="Enter custom category…"
                                    className={`${inputCls(false, false)} mt-1.5`}
                                  />
                                )}
                                {dCatWarn &&
                                  !KNOWN_CATEGORIES.has(editDraft.category) &&
                                  editDraft.category &&
                                  editDraft.category !== "__others__" && (
                                    <p className="text-[10px] text-[#D97706] mt-0.5">
                                      Custom category (saved as-is)
                                    </p>
                                  )}
                              </>
                            ) : (
                              <span className="text-[#6B7280]">
                                {row.category === "__others__"
                                  ? "Others"
                                  : row.category || "—"}
                              </span>
                            )}
                          </td>

                          {/* Expiry Date */}
                          <td className="px-3 py-2 align-top">
                            {isEditing ? (
                              <>
                                <input
                                  type="date"
                                  value={editDraft.expiry_date}
                                  onChange={(e) =>
                                    updateDraftField(
                                      "expiry_date",
                                      e.target.value
                                    )
                                  }
                                  className={inputCls(dDateErr, dDateWarn)}
                                />
                                {dDateErr && (
                                  <p className="text-[10px] text-[#DC2626] mt-0.5">
                                    Invalid format
                                  </p>
                                )}
                                {dDateWarn && !dDateErr && (
                                  <p className="text-[10px] text-[#D97706] mt-0.5">
                                    Missing
                                  </p>
                                )}
                              </>
                            ) : (
                              <span className="text-[#6B7280]">
                                {row.expiry_date || "—"}
                              </span>
                            )}
                          </td>

                          {/* Owner Email */}
                          <td className="px-3 py-2 align-top">
                            {isEditing ? (
                              <>
                                <input
                                  type="email"
                                  value={editDraft.owner_email}
                                  placeholder="owner@example.com"
                                  onChange={(e) =>
                                    updateDraftField(
                                      "owner_email",
                                      e.target.value
                                    )
                                  }
                                  className={inputCls(dEmailErr, false)}
                                />
                                {dEmailErr && (
                                  <p className="text-[10px] text-[#DC2626] mt-0.5">
                                    Invalid email
                                  </p>
                                )}
                              </>
                            ) : (
                              <span className="text-[#6B7280] max-w-[140px] truncate block">
                                {row.owner_email || "—"}
                              </span>
                            )}
                          </td>

                          {/* Status */}
                          <td className="px-3 py-2 align-top">
                            <div className="flex flex-col gap-1">
                              <RowBadge status={liveStatus} />
                              {liveIssues.map((issue, j) => (
                                <p
                                  key={j}
                                  className="text-[10px] text-[#6B7280] leading-tight"
                                >
                                  {issue}
                                </p>
                              ))}
                            </div>
                          </td>

                          {/* Edit / Save+Cancel actions */}
                          <td className="px-2 py-2 align-top">
                            {isEditing ? (
                              <div className="flex flex-col gap-1 pt-0.5">
                                {/* Save */}
                                <button
                                  type="button"
                                  onClick={saveEdit}
                                  title="Save"
                                  className="p-1 rounded text-[#15803D] bg-[#DCFCE7] hover:bg-[#BBF7D0] transition-colors"
                                >
                                  <svg
                                    className="h-3.5 w-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2.5}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                </button>
                                {/* Cancel */}
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  title="Cancel"
                                  className="p-1 rounded text-[#DC2626] bg-[#FEE2E2] hover:bg-[#FECACA] transition-colors"
                                >
                                  <svg
                                    className="h-3.5 w-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2.5}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => startEdit(row)}
                                title="Edit this row"
                                className="p-1 rounded text-[#9CA3AF] hover:text-[#2563EB] hover:bg-[#EFF6FF] transition-colors"
                              >
                                <svg
                                  className="h-3.5 w-3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {importError && (
                <p className="px-6 py-2 text-sm text-[#DC2626]">
                  {importError}
                </p>
              )}
            </div>
          )}

          {/* ── Step: Fix ───────────────────────────────────────────────── */}
          {step === "fix" && (
            <div className="flex flex-col">
              {/* Explainer */}
              <div className="px-6 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB] flex items-start gap-3">
                <svg
                  className="h-4 w-4 text-[#D97706] mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="text-xs text-[#374151] space-y-0.5">
                  <p>
                    <span className="font-semibold text-[#DC2626]">
                      Error rows
                    </span>{" "}
                    (missing Name or invalid date/email) are excluded from
                    import unless corrected here.
                  </p>
                  <p>
                    <span className="font-semibold text-[#D97706]">
                      Warning rows
                    </span>{" "}
                    (missing expiry / unknown category) will be imported as{" "}
                    <strong>drafts</strong> — fix them now or update later in
                    the tracker.
                  </p>
                </div>
              </div>

              {/* Live progress strip */}
              {fixedInStep > 0 && (
                <div className="px-6 py-2 bg-[#F0FDF4] border-b border-[#BBF7D0] flex items-center gap-2 text-xs text-[#15803D] font-semibold">
                  <svg
                    className="h-3.5 w-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {fixedInStep} of {editRows.length} row
                  {editRows.length !== 1 ? "s" : ""} fixed
                  {stillProblemCount > 0 && (
                    <span className="font-normal text-[#6B7280] ml-1">
                      — {stillProblemCount} still need attention
                    </span>
                  )}
                </div>
              )}

              {/* Editable table */}
              <div className="overflow-y-auto max-h-[400px]">
                <table className="min-w-full text-xs">
                  <thead className="bg-[#F9FAFB] sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2.5 text-left font-semibold text-[#6B7280] uppercase tracking-wider w-10">
                        #
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold text-[#6B7280] uppercase tracking-wider w-8">
                        <span className="sr-only">Status</span>
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold text-[#6B7280] uppercase tracking-wider">
                        Name <span className="text-[#DC2626]">*</span>
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold text-[#6B7280] uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold text-[#6B7280] uppercase tracking-wider">
                        Expiry Date
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold text-[#6B7280] uppercase tracking-wider">
                        Owner Email
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold text-[#6B7280] uppercase tracking-wider">
                        Notes
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {editRows.map((row) => {
                      const nameErr = hasIssueKeyword(
                        row.issues,
                        "name is required"
                      );
                      const dateErr = hasIssueKeyword(
                        row.issues,
                        "not recognised"
                      );
                      const dateWarn = hasIssueKeyword(
                        row.issues,
                        "no expiry date"
                      );
                      const catWarn = hasIssueKeyword(
                        row.issues,
                        "not in standard"
                      );
                      const emailErr = hasIssueKeyword(
                        row.issues,
                        "email format"
                      );

                      return (
                        <tr
                          key={row.rowNum}
                          className={
                            row.status === "valid"
                              ? "bg-[#F0FDF4]"
                              : row.status === "error"
                                ? "bg-[#FEF2F2]"
                                : "bg-[#FFFBEB]"
                          }
                        >
                          {/* Row # */}
                          <td className="px-3 py-2 text-[#9CA3AF] align-top pt-3">
                            {row.rowNum}
                          </td>

                          {/* Status dot */}
                          <td className="px-2 py-2 align-top pt-3">
                            <span
                              className={`inline-block h-2 w-2 rounded-full ${
                                row.status === "valid"
                                  ? "bg-[#15803D]"
                                  : row.status === "warning"
                                    ? "bg-[#D97706]"
                                    : "bg-[#DC2626]"
                              }`}
                            />
                          </td>

                          {/* Name */}
                          <td className="px-3 py-2 min-w-[150px]">
                            <input
                              type="text"
                              value={row.name}
                              placeholder="Document name"
                              onChange={(e) =>
                                updateEditField(
                                  row.rowNum,
                                  "name",
                                  e.target.value
                                )
                              }
                              className={inputCls(nameErr, false)}
                            />
                            {nameErr && (
                              <p className="text-[10px] text-[#DC2626] mt-0.5">
                                Required
                              </p>
                            )}
                          </td>

                          {/* Category */}
                          <td className="px-3 py-2 min-w-[160px]">
                            <div className="relative">
                              <select
                                value={
                                  KNOWN_CATEGORIES.has(row.category)
                                    ? row.category
                                    : row.category
                                      ? "__others__"
                                      : ""
                                }
                                onChange={(e) => {
                                  const v = e.target.value;
                                  updateEditField(
                                    row.rowNum,
                                    "category",
                                    v === "__others__" ? "__others__" : v
                                  );
                                }}
                                className={`${inputCls(false, catWarn)} pr-6 cursor-pointer appearance-none`}
                              >
                                <option value="">Select category…</option>
                                {DOCUMENT_CATEGORIES.map((c) => (
                                  <option key={c} value={c}>
                                    {c}
                                  </option>
                                ))}
                                <option value="__others__">Custom…</option>
                              </select>
                              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                                <svg
                                  className="h-3 w-3 text-[#9CA3AF]"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </div>
                            </div>
                            {!KNOWN_CATEGORIES.has(row.category) && (
                              <input
                                type="text"
                                value={
                                  row.category === "__others__"
                                    ? ""
                                    : row.category
                                }
                                onChange={(e) =>
                                  updateEditField(
                                    row.rowNum,
                                    "category",
                                    e.target.value || "__others__"
                                  )
                                }
                                placeholder="Enter custom category…"
                                className={`${inputCls(false, false)} mt-1.5`}
                              />
                            )}
                            {catWarn &&
                              !KNOWN_CATEGORIES.has(row.category) &&
                              row.category &&
                              row.category !== "__others__" && (
                                <p className="text-[10px] text-[#D97706] mt-0.5">
                                  Custom category (saved as-is)
                                </p>
                              )}
                          </td>

                          {/* Expiry Date */}
                          <td className="px-3 py-2 min-w-[140px]">
                            <input
                              type="date"
                              value={row.expiry_date}
                              onChange={(e) =>
                                updateEditField(
                                  row.rowNum,
                                  "expiry_date",
                                  e.target.value
                                )
                              }
                              className={inputCls(dateErr, dateWarn)}
                            />
                            {dateErr && (
                              <p className="text-[10px] text-[#DC2626] mt-0.5">
                                Invalid format
                              </p>
                            )}
                            {dateWarn && !dateErr && (
                              <p className="text-[10px] text-[#D97706] mt-0.5">
                                Missing — will be a draft
                              </p>
                            )}
                          </td>

                          {/* Owner Email */}
                          <td className="px-3 py-2 min-w-[150px]">
                            <input
                              type="email"
                              value={row.owner_email}
                              placeholder="owner@example.com"
                              onChange={(e) =>
                                updateEditField(
                                  row.rowNum,
                                  "owner_email",
                                  e.target.value
                                )
                              }
                              className={inputCls(emailErr, false)}
                            />
                            {emailErr && (
                              <p className="text-[10px] text-[#DC2626] mt-0.5">
                                Invalid email
                              </p>
                            )}
                          </td>

                          {/* Notes */}
                          <td className="px-3 py-2 min-w-[140px]">
                            <input
                              type="text"
                              value={row.notes}
                              placeholder="Optional notes"
                              onChange={(e) =>
                                updateEditField(
                                  row.rowNum,
                                  "notes",
                                  e.target.value
                                )
                              }
                              className={inputCls(false, false)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Step: Importing ─────────────────────────────────────────── */}
          {step === "importing" && (
            <div className="px-6 py-16 flex flex-col items-center gap-4">
              <div className="animate-spin h-10 w-10 rounded-full border-4 border-[#E5E7EB] border-t-[#2563EB]" />
              <p className="text-sm font-medium text-[#374151]">
                Importing {importable.length} documents…
              </p>
              <p className="text-xs text-[#9CA3AF]">
                This will only take a moment.
              </p>
            </div>
          )}

          {/* ── Step: Done ──────────────────────────────────────────────── */}
          {step === "done" && (
            <div className="px-6 py-16 flex flex-col items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-[#DCFCE7] flex items-center justify-center">
                <svg
                  className="h-7 w-7 text-[#15803D]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div className="text-center">
                <p className="text-base font-semibold text-[#111827]">
                  Import complete!
                </p>
                <p className="text-sm text-[#6B7280] mt-1">
                  {importable.length} document
                  {importable.length !== 1 ? "s" : ""} added to your tracker.
                  {warningCount > 0 &&
                    ` ${warningCount} saved as draft${warningCount > 1 ? "s" : ""} (missing fields).`}
                  {errorCount > 0 &&
                    ` ${errorCount} row${errorCount > 1 ? "s" : ""} with errors were excluded.`}
                </p>
              </div>
            </div>
          )}

          {/* ── Footer ─────────────────────────────────────────────────── */}
          {step !== "importing" && (
            <div className="bg-[#F9FAFB] border-t border-[#E5E7EB] px-6 py-4 flex items-center justify-between">
              <div>
                {step === "review" && (
                  <button
                    type="button"
                    onClick={resetUpload}
                    className="text-sm text-[#6B7280] hover:text-[#374151] transition-colors flex items-center gap-1.5"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    Upload a different file
                  </button>
                )}
                {step === "fix" && (
                  <button
                    type="button"
                    onClick={() => setStep("review")}
                    className="text-sm text-[#6B7280] hover:text-[#374151] transition-colors flex items-center gap-1.5"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    Back without applying
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB] transition-colors"
                >
                  {step === "done" ? "Close" : "Cancel"}
                </button>

                {step === "fix" && (
                  <button
                    type="button"
                    onClick={applyFixes}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#2563EB] rounded-md hover:bg-[#1D4ED8] transition-colors"
                  >
                    Apply Fixes & Review
                  </button>
                )}

                {step === "review" && (
                  <button
                    type="button"
                    disabled={importable.length === 0}
                    onClick={handleConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#2563EB] rounded-md hover:bg-[#1D4ED8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Import {importable.length} document
                    {importable.length !== 1 ? "s" : ""}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
